import { Box, Flex, Tooltip } from '@chakra-ui/react';
import { useEffect, useRef } from 'react';
import { RiImage2Line } from 'react-icons/ri';
import { useUploadInlineImageMutation } from '@/store/query/mco-step';
import { useShowToast } from '@/hooks/useShowToast';
import { useKFlags } from '@/hooks/use-k-flags';

export const ImageButton = ({
  editor,
  setImageLoading = () => {},
  isDisabled = false,
  disabledMessage = 'Inline images are not supported here',
  styles = {},
  isMaxImageCountExceeded = false,
  iconColor = '#121314',
}) => {
  const imageRef = useRef();
  const toast = useShowToast();
  const [uploadInlineImage, { isLoading }] = useUploadInlineImageMutation();
  const { ats_enabled } = useKFlags();

  useEffect(() => {
    setImageLoading(isLoading);
  }, [isLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  const onImageChange = async (e) => {
    if (!e?.target?.files?.length) return;
    if (isDisabled) {
      toast({
        title: 'Maximum number of files exceeded',
        position: 'bottom-left',
        status: 'error',
        isClosable: true,
      });
      return;
    }
    if (e?.target?.files[0]?.size / 1024 / 1024 > 10) {
      e.target.value = '';

      toast({
        title:
          'The file size limit is 10MB per file. Consider compressing or choosing another file.',
        position: 'bottom-left',
        status: 'error',
        isClosable: true,
      });
      return;
    }
    const res = await uploadInlineImage({ file: e?.target?.files[0] });
    if (!res?.data?.image_url) {
      toast({
        title: 'File upload unsuccessful. Please try again.',
        position: 'bottom-left',
        status: 'error',
        isClosable: true,
      });
      return;
    }
    editor
      .chain()
      .focus()
      .setImage({
        src: res?.data?.image_url,
        'data-og-width': res?.data?.metadata?.resolution?.width,
        'data-og-height': res?.data?.metadata?.resolution?.height,
      })
      .run();
    e.target.value = '';
  };

  const defaultMsg =
    '"Supported file formats are .jpg, .png, and .gif, with a maximum file size of 10 MB"';

  return (
    <Flex alignItems="center">
      <input
        type="file"
        ref={imageRef}
        onChange={(e) => onImageChange(e)}
        accept="image/gif, image/jpeg, image/png"
        style={{ visibility: 'hidden', height: 0, width: 0 }}
        id="editor-image-input"
        data-test-id="editor-image-input"
        disabled={isDisabled}
      />
      <Tooltip
        label={
          isMaxImageCountExceeded
            ? 'Maximum number of images allowed exceeded'
            : isDisabled
            ? disabledMessage || defaultMsg
            : defaultMsg
        }
        hasArrow
        placement="top"
        variant={ats_enabled && 'ats-primary'}
        borderRadius="4px"
      >
        <Box
          role="button"
          onClick={() => imageRef?.current?.click()}
          borderRadius="6px"
          cursor={isDisabled ? 'not-allowed' : 'pointer'}
          p="4px 8px"
          opacity={isDisabled ? 0.5 : 1}
          disabled={isDisabled}
        >
          <RiImage2Line className="toolbar-icon" w="18px" h="18px" />
        </Box>
      </Tooltip>
    </Flex>
  );
};
